import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { authAPI } from '../services/api'
import { useAuthStore } from '../store/authStore'
import Spinner from '../components/Spinner'
import AuthShell, {
  authButtonClass,
  authInputClass,
  authLabelClass,
  authLinkClass,
  authSpinnerClass,
} from '../components/auth/AuthShell'

const Login = () => {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [challengeToken, setChallengeToken] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const setAuth = useAuthStore((state) => state.setAuth)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      const data = await authAPI.login(username, password)

      if (data.two_factor_required) {
        setChallengeToken(data.challenge_token)
        toast.success('Enter the verification code sent to your phone')
        return
      }

      setAuth(data.access_token, await authAPI.getProfile(data.access_token), data.refresh_token)
      toast.success('Login successful!')
      navigate('/')
    } catch (error) {
      toast.error('Login failed. Please check your credentials.')
    } finally {
      setLoading(false)
    }
  }

  const handleConfirmCode = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!challengeToken) return
    setLoading(true)

    try {
      const { access_token, refresh_token } = await authAPI.confirmTwoFactor(challengeToken, code)
      const user = await authAPI.getProfile(access_token)
      setAuth(access_token, user, refresh_token)
      toast.success('Login successful!')
      navigate('/')
    } catch (error) {
      toast.error('Invalid or expired verification code.')
    } finally {
      setLoading(false)
    }
  }

  if (challengeToken) {
    return (
      <AuthShell
        heading="Two-Factor Verification"
        subheading="Enter the 6-digit code we texted to your phone"
      >
        <form onSubmit={handleConfirmCode} className="space-y-6">
          <div>
            <label htmlFor="code" className={authLabelClass}>
              Verification code
            </label>
            <input
              id="code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className={`${authInputClass} text-center font-mono tracking-[0.4em]`}
              required
            />
          </div>

          <button type="submit" disabled={loading} className={authButtonClass}>
            {loading && <Spinner size="sm" className={authSpinnerClass} />}
            {loading ? 'Verifying' : 'Verify'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => {
              setChallengeToken(null)
              setCode('')
            }}
            className={`text-sm ${authLinkClass}`}
          >
            Back to sign in
          </button>
        </div>
      </AuthShell>
    )
  }

  return (
    <AuthShell>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="username" className={authLabelClass}>
            Username
          </label>
          <input
            id="username"
            type="text"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className={authInputClass}
            required
          />
        </div>

        <div>
          <div className="flex items-center justify-between">
            <label htmlFor="password" className={authLabelClass}>
              Password
            </label>
            <Link to="/forgot-password" className={`text-sm ${authLinkClass}`}>
              Forgot password?
            </Link>
          </div>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={authInputClass}
            required
          />
        </div>

        <button type="submit" disabled={loading} className={authButtonClass}>
          {loading && <Spinner size="sm" className={authSpinnerClass} />}
          {loading ? 'Signing in' : 'Sign in'}
        </button>
      </form>

      <div className="mt-6 text-center">
        <p className="text-sm text-gray-400">
          Don't have an account?{' '}
          <Link to="/register" className={authLinkClass}>
            Sign up
          </Link>
        </p>
      </div>
    </AuthShell>
  )
}

export default Login
